import { useCallback, useMemo } from 'react';

import { useHistory, useLocation } from 'react-router';

import { EventTypes } from '@/constants/types';
import { ApprovalStatus } from '@/graphql';
import { useAsOperator } from '@/hooks';
import { useUserLoading } from '@/hooks/useUser';
import { useEventLogger } from '@/legacy/store/actions/hooks';
import { WorkspaceSections } from '@/pages/Workspace/types';
import { BasetenPageEnum, usePageSwitcher } from '@/routes';
import { useSnackbar } from 'notistack';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';

import { onboardingStep, profileDataState } from './atoms';
import { OnboardingStep } from './types';
import { completeSignup } from './utils';

function useCurrentStep() {
  const { user } = useUserLoading();
  const step = useRecoilValue(onboardingStep);

  return (
    step ||
    (user?.status === ApprovalStatus.PendingOrgApproval
      ? OnboardingStep.RequestedToJoin
      : OnboardingStep.Profile)
  );
}

function useCompleteOnboarding() {
  const logEvent = useEventLogger();
  const { enqueueSnackbar } = useSnackbar();
  const { refetch } = useUserLoading();

  return useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const profileData = await snapshot.getPromise(profileDataState);

        try {
          await completeSignup(profileData);
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
          });
          return;
        }

        logEvent(EventTypes.FINISHED_ONBOARDING_FLOW);
        refetch();
      },
    [logEvent, refetch, enqueueSnackbar],
  );
}

function useProfileData() {
  return useRecoilState(profileDataState);
}

function useNextQueryParam() {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  return query.get('next');
}

function useExitOnboarding() {
  const goToPage = usePageSwitcher();
  const isOperator = useAsOperator();
  const history = useHistory();
  const nextPage = useNextQueryParam();

  return useCallback(() => {
    if (nextPage) {
      if (nextPage.startsWith('/o')) {
        // OAuth paths are not part of our React router so we handle it manually.
        window.location.href = `${window.location.origin}${nextPage}`;
      } else {
        history.replace(nextPage);
      }
    } else {
      goToPage(BasetenPageEnum.Workspace, {
        section: isOperator ? WorkspaceSections.Applications : WorkspaceSections.Models,
      });
    }
  }, [goToPage, isOperator, history, nextPage]);
}

export { useProfileData, useCompleteOnboarding, useCurrentStep, useExitOnboarding };
